import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';

// Components
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import ProgramsSection from "./components/ProgramsSection";
import WhyOwazaSection from "./components/WhyOwazaSection";
import ScheduleSection from "./components/ScheduleSection";
import FaqSection from "./components/FaqSection";
import ReviewsSection from "./components/ReviewsSection";
import ExpectationSection from "./components/ExpectationSection";
import LocationSection from "./components/LocationSection";
import Footer from "./components/Footer";
import AdminApp from "./components/admin/AdminApp";
import LineagePage from "./components/LineagePage";
import GalleryPage from "./components/GalleryPage";
import CustomPage from "./components/CustomPage";

// Customer Portal
import CustomerPortal from "./components/customer/CustomerPortal";
import CustomerLogin from "./components/customer/CustomerLogin";
import MagicLinkVerify from "./components/customer/MagicLinkVerify";
import PaymentSuccess from "./components/customer/PaymentSuccess";
import PaymentCancelled from "./components/customer/PaymentCancelled";

// Newsletter
import { NewsletterArchive, NewsletterView } from "./components/NewsletterArchive";

// QR Code Landing Pages
import {
  ReviewRedirect,
  FeedbackForm,
  PhotoUpload,
  EventsPage,
  EventDetailPage,
  ShareExperience,
} from "./components/public";

const Home = () => {
  useEffect(() => {
    // Handle hash scrolling after page loads
    const scrollToHash = () => {
      const hash = window.location.hash;
      if (hash) {
        // Remove the # to get the id
        const id = hash.replace('#', '');
        
        // Try multiple times with increasing delays to handle async content loading
        const attemptScroll = (attempt = 0) => {
          const element = document.getElementById(id);
          if (element) {
            // Scroll with offset to account for fixed header
            const headerOffset = 80; // Adjust based on your header height
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          } else if (attempt < 5) {
            // If element not found, try again after a delay
            setTimeout(() => attemptScroll(attempt + 1), 100 * (attempt + 1));
          }
        };
        
        // Start attempting to scroll
        setTimeout(() => attemptScroll(), 100);
      }
    };

    scrollToHash();
    
    // Also handle hash changes (when clicking hash links on same page)
    window.addEventListener('hashchange', scrollToHash);
    
    return () => {
      window.removeEventListener('hashchange', scrollToHash);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <HeroSection />
      <ProgramsSection />
      <WhyOwazaSection />
      <ScheduleSection />
      <FaqSection />
      <ReviewsSection />
      <ExpectationSection />
      <LocationSection />
      <Footer />
    </div>
  );
};

const LineageWrapper = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <LineagePage />
      <Footer />
    </div>
  );
};

const GalleryWrapper = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <GalleryPage />
      <Footer />
    </div>
  );
};

const CustomPageWrapper = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <CustomPage />
      <Footer />
    </div>
  );
};

const EventsWrapper = ({ children }) => {
  return (
    <div className="min-h-screen">
      <Header />
      {children}
      <Footer />
    </div>
  );
};

const NewsletterArchiveWrapper = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <NewsletterArchive />
      <Footer />
    </div>
  );
};

const NewsletterViewWrapper = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <NewsletterView />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/lineage" element={<LineageWrapper />} />
            <Route path="/gallery" element={<GalleryWrapper />} />
            <Route path="/page/:slug" element={<CustomPageWrapper />} />
            <Route path="/admin" element={<AdminApp />} />
            {/* Customer Portal Routes */}
            <Route path="/account" element={<CustomerPortal />} />
            <Route path="/account/login" element={<CustomerLogin />} />
            <Route path="/account/verify" element={<MagicLinkVerify />} />
            <Route path="/account/payment-success" element={<PaymentSuccess />} />
            <Route path="/account/payment-cancelled" element={<PaymentCancelled />} />
            {/* Newsletter Archive Routes */}
            <Route path="/newsletter/:token" element={<NewsletterArchiveWrapper />} />
            <Route path="/newsletter/:token/:id" element={<NewsletterViewWrapper />} />
            {/* QR Code Landing Pages */}
            <Route path="/review" element={<ReviewRedirect />} />
            <Route path="/feedback" element={<FeedbackForm />} />
            <Route path="/upload" element={<PhotoUpload />} />
            <Route path="/events" element={<EventsWrapper><EventsPage /></EventsWrapper>} />
            <Route path="/events/:eventId" element={<EventsWrapper><EventDetailPage /></EventsWrapper>} />
            <Route path="/share" element={<ShareExperience />} />
          </Routes>
        </BrowserRouter>
      </div>
    </GoogleOAuthProvider>
  );
}

export default App;
